import { Input, OnInit, ViewChild } from '@angular/core';
import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, tap } from 'rxjs/operators';
import { IClassifier } from '../../models/Classifier';

import { IThesisReport, IThesisReportDoctor, IThesisReportEditModel } from '../../models/ThesisReport';
import { AppService } from '../../services/app.service';
import { ClassifierService } from '../../services/classifier.service';
import { ParameterService } from '../../services/parameter.service';
import { ThesisReportService } from '../../services/thesis-report.service';

import * as FileSaver from 'file-saver';
import { createFileStub } from '../../shared/file/file.component';
import { clearSummaryCache } from './summary.component';

interface IAttachment {
    id?: number;
    file?: File;
    fileName?: string;
}

interface IPreparer {
    id: string;
    name: string;
}

const cache: { doctors?: IThesisReportDoctor[] } = {};

@Component({
    selector: 'app-thesis-report-edit',
    templateUrl: './edit.component.html'
})
export class ThesisReportEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: ThesisReportService,
        private parameters: ParameterService,
        private classifiers: ClassifierService
    ) { }

    @Input() report?: IThesisReport;

    isNew: boolean;
    doctor: IThesisReportDoctor;
    doctors: IThesisReportDoctor[] = [];
    cooperationGradeId: string;
    grades: IClassifier[] = [];
    progress: string;
    results: string;
    hasScientificActivity: boolean;
    hasTeachingActivity: boolean;
    preparer: IPreparer;
    preparers: IPreparer[] = [];

    attachments: IAttachment[] = [];
    attachmentMaxSize: number = 0;
    attachmentLimit: number = 0;
    attachmentExtensions: string = '';

    readonly doctorDisplayFn = (option: IThesisReportDoctor) => `${option.FirstName} ${option.LastName}, ${option.CertificateNumber}`;

    readonly doctorFilterFn = (option: IThesisReportDoctor, term: string) => {
        term = term.toLowerCase();

        return option.FirstName.toLowerCase().includes(term)
            || option.LastName.toLowerCase().includes(term)
            || option.Upn.toLowerCase().includes(term)
            || option.CertificateNumber.toLowerCase().includes(term);
    }

    private id: number;
    private removedAttachments: IAttachment[] = [];
    private originalData: string;
    private isAdmin: boolean;

    @ViewChild('form', { static: false }) private form: NgForm;

    ngOnInit() {
        this.isAdmin = this.app.currentUser.hasRight('THESIS_REPORT.ADMIN');

        if (this.report) {
            this.id = this.report.Id;
            this.progress = this.report.Progress;
            this.results = this.report.Results;
            this.hasScientificActivity = this.report.HasScientificActivity;
            this.hasTeachingActivity = this.report.HasTeachingActivity;
            this.cooperationGradeId = this.report.CooperationGradeId;

            this.attachments = this.report.Attachments.map(t => {
                return {
                    id: t.Id,
                    file: createFileStub(t.FileName),
                    fileName: t.FileName
                };
            });
        } else {
            this.isNew = true;
        }

        this.loadDoctors();

        this.app.addLoading(this.parameters.getValues()).subscribe(data => {
            this.attachmentMaxSize = this.parameters.findValue(data, 'ThesisReportFileMaxSize', t => +t, 0);
            this.attachmentLimit = this.parameters.findValue(data, 'ThesisReportFileLimit', t => +t, 0);
            this.attachmentExtensions = this.parameters.findValue(data, 'ThesisReportFileExtensions',
                t => '.' + t.split(',').join(',.'), '');
        });

        this.app.addLoading(this.classifiers.get('Grade')).subscribe(data => {
            const grades = data.filter(t => +t.Code);
            grades.sort((a, b) => (+a.Code) - (+b.Code));
            this.grades = grades;
        });
    }

    downloadAttachment(attachment: IAttachment) {
        this.app.addLoading(this.service.getAttachment(attachment.id)).subscribe(result => {
            FileSaver.saveAs(result, attachment.file.name);
        });
    }

    addAttachment() {
        this.attachments.push(<IAttachment>{});
    }

    removeAttachment(file: IAttachment) {
        let i = this.attachments.indexOf(file);

        if (i > -1) {
            this.attachments.splice(i, 1);

            if (file.id) {
                this.removedAttachments.push(file);
            }
        }
    }

    onFileChange(event: File, file: IAttachment) {
        if (!event) {
            this.removeAttachment(file);
        } else {
            file.fileName = event.name;
        }
    }

    checkDuplicateFileName(file: IAttachment): boolean {
        if (!file.file) return false;
        return this.attachments.filter(t => t.file?.name == file.file.name).length > 1;
    }

    validate(mode: 'save' | 'submit'): boolean {
        if (!this.form.valid) {
            this.app.alert.error(this.app.translate('thesisReport_formError'));
            return false;
        }

        if (mode == 'submit') {
            const required: string[] = [];

            if (!(this.progress || '').replace(/\s/g, '').length) {
                required.push('thesisReport_progressRequired');
            }

            if (!this.cooperationGradeId) {
                required.push('thesisReport_cooperationGradeRequired');
            }

            if (required.length) {
                this.app.alert.error(required.map(t => this.app.translate(t)).join('<br>'));
                return false;
            }
        }

        for (let i = 0; i < this.attachments.length; i++) {
            if (this.attachments[i].file) {
                for (let j = i + 1; j < this.attachments.length; j++) {
                    if (this.attachments[j] && this.attachments[i].file.name == this.attachments[j].file.name) {
                        this.app.alert.error(this.app.translate('thesisReport_fileNamesMustBeUnique'));
                        return false;
                    }
                }
            }
        }

        return true;
    }

    save() {
        if (!this.validate('save')) return;

        this.createOrUpdate().subscribe(() => {
            this.app.notify(this.app.translate('thesisReport_saved'));
            this.app.navigate(['/thesis-reports']);

            clearSummaryCache();
        });
    }

    submit() {
        if (!this.validate('submit')) return;

        this.app.confirm({
            text: this.app.translate('thesisReport_submitText'),
            title: this.app.translate('thesisReport_submitTitle'),
            okText: this.app.translate('thesisReport_submitOk')
        }, result => {
            if (!result) return;

            this.createOrUpdate().subscribe(id => {
                this.app.addLoading(this.service.submit(id)).subscribe(() => {
                    this.app.notify(this.app.translate('thesisReport_submitted'));
                    this.app.navigate(['/thesis-reports']);
                }, err => {
                    this.app.showError(err);
                });
            });
        });
    }

    getLengthValidationPattern(value: string, min: number, max: number, allowEmpty: boolean = false): string {
        const len = (value || '').length;
        if (!len && allowEmpty) return '';
        return (len >= min && len <= max) ? '' : '$^';
    }

    getSupervisorDisplay(item: IThesisReportDoctor) {
        return item.Supervisors.map(t => t.Name).join(', ');
    }

    onDoctorChange() {
        this.populatePreparers(this.doctor);
    }

    private populatePreparers(doctor: IThesisReportDoctor) {
        const preparers: IPreparer[] = [
            {
                id: null,
                name: `${this.app.currentUser.firstName} ${this.app.currentUser.lastName}`
            }
        ];

        let currentId: string;

        if (doctor) {
            currentId = this.report?.Supervisors.find(t => t.IsPreparer)?.Id;

            preparers.push(...doctor.Supervisors.filter(t => t.CanPrepare).map(t => {
                return {
                    id: t.Id,
                    name: t.Name
                }
            }));
        }

        this.preparers = preparers;
        this.preparer = preparers.find(t => t.id == currentId) || preparers[0];
    }

    private createOrUpdate() {
        const subj = new Subject<number>();

        const payload: IThesisReportEditModel = {
            StudentId: this.doctor.StudentId,
            PreparerId: this.preparer?.id,
            HasScientificActivity: this.hasScientificActivity,
            HasTeachingActivity: this.hasTeachingActivity,
            Progress: this.progress,
            Results: this.results,
            CooperationGradeId: this.cooperationGradeId
        };

        const saveAttachments = (id: number) => {
            this.app.addLoading(this.saveAttachments(id)).subscribe(files => {
                const errors = files.filter(f => f.error).map(f => {
                    return `<p><strong>${f.fileName}</strong><br>${f.error}</p>`;
                });

                if (errors.length) {
                    this.app.showError(errors.join(''));
                } else {
                    subj.next(id);
                }
            });
        };

        const after = JSON.stringify(payload);

        if (this.id) {
            if (after != this.originalData) {
                this.app.addLoading(this.service.update(this.id, payload)).subscribe(() => {
                    this.originalData = after;
                    saveAttachments(this.id);
                });
            } else {
                setTimeout(() => {
                    subj.next(this.id);
                }, 100);
            }
        } else {
            this.app.addLoading(this.service.create(payload)).subscribe(id => {
                this.id = id;
                this.originalData = after;
                saveAttachments(id);
            });
        }

        return subj.asObservable();
    }

    private saveAttachments(id: number) {
        const results: {
            fileName: string,
            added?: boolean,
            removed?: boolean,
            error?: string
        }[] = [];

        const subj = new Subject<typeof results>();

        const remove = this.removedAttachments.map(t => {
            results.push({ fileName: t.fileName });

            const entry = results[results.length - 1];

            return this.service.removeAttachment(t.id).pipe(tap(() => {
                entry.removed = true;
            }), catchError(err => {
                entry.removed = false;
                entry.error = this.app.getHttpResponseError(err);

                return of(null);
            }));
        });

        const add = this.attachments.filter(t => !!t.file?.size).map(t => {
            results.push({ fileName: t.file.name });

            const entry = results[results.length - 1];

            return this.service.addAttachment(id, t.file).pipe(tap(() => {
                entry.added = true;

                // drop the file to prevent from re-upload if saving again after error
                t.file = createFileStub(t.file.name);
            }), catchError(err => {
                entry.added = false;
                entry.error = this.app.getHttpResponseError(err);

                return of(null);
            }));
        });

        if (!remove.length && !add.length) return of(results);

        forkJoin(...remove).pipe(
            defaultIfEmpty(null),
        ).subscribe(() => {
            forkJoin(...add).pipe(
                defaultIfEmpty(null),
            ).subscribe(() => {
                subj.next(results);
            });
        });

        return subj.asObservable();
    }

    private loadDoctors() {
        const done = (data: IThesisReportDoctor[]) => {
            this.doctors = cache.doctors = data;

            if (this.report) {
                this.doctor = data.find(t => t.StudentId == this.report.StudentId);

                if (this.isAdmin) {
                    this.populatePreparers(this.doctor);
                }
            }
        };

        if (cache.doctors) {
            done(cache.doctors);
        } else {
            this.app.addLoading(this.service.getDoctors()).subscribe(data => {
                done(data);
            });
        }
    }
}
